import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { X, AlertTriangle, ArrowRight } from 'lucide-react';
import { admin as adminApi } from '../utils/api';

const MergeUsersModal = ({ onClose, onSuccess, allUsers }) => {
  const [sourceUserId, setSourceUserId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [loading, setLoading] = useState(false);

  const clientUsers = allUsers.filter(u => u.role === 'client');
  const sourceUser = clientUsers.find(u => u.id === sourceUserId);
  const targetUser = clientUsers.find(u => u.id === targetUserId);

  const handleMerge = async () => {
    if (!sourceUserId || !targetUserId) {
      toast.error('Please select both users');
      return;
    }

    if (sourceUserId === targetUserId) {
      toast.error('Cannot merge a user with themselves');
      return;
    }

    const confirmMessage = `Are you sure you want to merge ${sourceUser.first_name} ${sourceUser.last_name} into ${targetUser.first_name} ${targetUser.last_name}?\n\nThis will:\n- Transfer all pets from ${sourceUser.first_name} to ${targetUser.first_name}\n- Transfer all appointments from ${sourceUser.first_name} to ${targetUser.first_name}\n- Delete ${sourceUser.first_name}'s account\n\nThis action CANNOT be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const response = await adminApi.mergeUsers(sourceUserId, targetUserId);
      toast.success(response.data.message);
      onSuccess();
    } catch (error) {
      console.error('Merge users error:', error);
      toast.error(error.response?.data?.error || 'Failed to merge users');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Merge User Accounts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Warning: This action cannot be undone!</p>
              <p>Merging will transfer all pets and appointments from the source account to the target account, then delete the source account.</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Source User */}
          <div>
            <label className="label">
              Source Account (will be deleted after merge)
            </label>
            <select
              value={sourceUserId}
              onChange={(e) => setSourceUserId(e.target.value)}
              className="input"
              disabled={loading}
            >
              <option value="">Select user to merge from...</option>
              {clientUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
            {sourceUser && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-600">
                  <strong>Email:</strong> {sourceUser.email}
                </p>
                {sourceUser.phone && (
                  <p className="text-gray-600">
                    <strong>Phone:</strong> {sourceUser.phone}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Arrow */}
          {sourceUserId && targetUserId && (
            <div className="flex justify-center">
              <ArrowRight size={32} className="text-primary-600" />
            </div>
          )}

          {/* Target User */}
          <div>
            <label className="label">
              Target Account (will receive all pets and appointments)
            </label>
            <select
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value)}
              className="input"
              disabled={loading}
            >
              <option value="">Select user to merge into...</option>
              {clientUsers
                .filter(u => u.id !== sourceUserId)
                .map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
            </select>
            {targetUser && (
              <div className="mt-2 p-3 bg-green-50 rounded-lg text-sm">
                <p className="text-gray-600">
                  <strong>Email:</strong> {targetUser.email}
                </p>
                {targetUser.phone && (
                  <p className="text-gray-600">
                    <strong>Phone:</strong> {targetUser.phone}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Summary */}
          {sourceUser && targetUser && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Merge Summary:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ All pets from <strong>{sourceUser.first_name}</strong> will be transferred to <strong>{targetUser.first_name}</strong></li>
                <li>✓ All appointments from <strong>{sourceUser.first_name}</strong> will be transferred to <strong>{targetUser.first_name}</strong></li>
                <li>✓ <strong>{sourceUser.first_name}'s</strong> account will be deleted</li>
                <li>✓ <strong>{targetUser.first_name}</strong> will be able to see and manage all pets</li>
              </ul>
            </div>
          )}
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            className="flex-1 btn btn-primary"
            disabled={loading || !sourceUserId || !targetUserId}
          >
            {loading ? 'Merging...' : 'Merge Accounts'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergeUsersModal;
